import { IReturn } from "@/interfaces/Return";
import React from "react";
import ProductCounter from "../product-counter/ProductCounter";
import { formateDate } from "@/utils/date-utils";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PRIMARY_COLOR, SECONDARY_COLOR } from "@/constants/constants";
import { setReturn } from "@/store/reducers/returnReducer";
import { NavigationRoutes } from "../navigation/constants";
import DropOffTag from "../tags/DropoffTag";
import StatusTag from "../tags/StatusTag";
import SourceTag from "../tags/SourceTag";
import { Button } from "../ui/button";
import { Copy, Download } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { handleFileDownload } from "@/utils/file-download";
import ReturnTypesTag from "../tags/ReturnTypesTag";
import { ReturnStatus } from "@/enums";

interface CanceledReturnProps {
  return_: IReturn;
}

const CanceledReturn: React.FC<CanceledReturnProps> = ({ return_ }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleReturnClick_ = () => {
    dispatch(setReturn(return_));
    if (return_.statutId === ReturnStatus.CANCELED_BY_SELLER) {
      navigate(NavigationRoutes.CANCELED_RETURN_DETAILS);
    } else {
      navigate(NavigationRoutes.RETURN_DETAILS);
    }
  };

  const onDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (return_.url)
      handleFileDownload(return_.url, return_.numTournee.toString() || "");
  };

  const renderDownloadButton = () => {
    if (return_.url) {
      return (
        <Button
          className="flex items-center justify-center w-8 h-8 border rounded-md bg-white hover:bg-teal-50"
          style={{ borderColor: SECONDARY_COLOR }}
          onClick={onDownloadClick}
        >
          <Download className="w-4 h-4" style={{ color: PRIMARY_COLOR }} />
        </Button>
      );
    }
    return null;
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (return_.numPOScale) {
      navigator.clipboard.writeText(return_.numPOScale);
      toast({
        title: "Copie",
        description: "N° PO copier dans le presse-papier",
      });
    }
  };

  // N° PO et bouton de copie forment une seule pastille: on voit ainsi
  // clairement quelle valeur est copiee
  const renderPOScale = () => {
    if (!return_.numPOScale) return null;
    return (
      <div
        className="inline-flex items-stretch overflow-hidden border rounded-md bg-white"
        style={{ borderColor: SECONDARY_COLOR }}
      >
        <div className="flex items-center gap-1 px-2 py-1">
          <span
            className="text-xs font-semibold"
            style={{ color: SECONDARY_COLOR }}
          >
            N° PO:
          </span>
          <span className="text-xs font-bold text-gray-700 break-all">
            {return_.numPOScale}
          </span>
        </div>
        <Button
          title="Copier le N° PO"
          aria-label="Copier le N° PO"
          className="flex items-center justify-center h-auto px-2 py-1 border-l rounded-none bg-white hover:bg-teal-50"
          style={{ borderColor: SECONDARY_COLOR }}
          onClick={handleCopyClick}
        >
          <Copy className="w-4 h-4" style={{ color: PRIMARY_COLOR }} />
        </Button>
      </div>
    );
  };

  const renderParentTournee = () => {
    if (!return_.tourneeRetourParentId) return null;
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-md bg-gray-50">
        <span className="text-xs font-semibold text-gray-500">
          Tournee parent:
        </span>
        <span className="text-xs font-bold text-gray-700">
          N° {return_.tourneeRetourParentId}
        </span>
      </div>
    );
  };

  const hasIdentifiers = Boolean(
    return_.numPOScale || return_.tourneeRetourParentId,
  );

  return (
    <div
      className="w-full flex flex-col rounded-lg shadow-md bg-white hover:bg-gray-50 my-2 border-2 p-3 space-y-2 cursor-pointer transition-colors duration-200"
      onClick={handleReturnClick_}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-x-3 gap-y-2 w-full">
        <h2 className="text-lg font-semibold text-gray-800 whitespace-nowrap shrink-0">
          Tournee N° {return_.numTournee}
        </h2>
        <div className="flex flex-wrap items-center gap-1.5 sm:flex-1 sm:min-w-0 sm:justify-end">
          <ReturnTypesTag text={return_.typeTournee} />
          <SourceTag text={return_.sourceVendable} />
          <DropOffTag
            entityId={return_.entityId}
            entityName={return_.entityName}
          />
          <StatusTag
            text={return_.statutName}
            bakcgroundColor={return_.statutBackground}
          />
        </div>
      </div>

      {hasIdentifiers && (
        <div className="flex flex-wrap items-center gap-2">
          {renderPOScale()}
          {renderParentTournee()}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-gray-700">
          {return_.sellerName}
        </h3>
        <p className="text-xs text-gray-500 shrink-0">
          {formateDate(return_.createdon)}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ProductCounter productCount={return_.countArticles} />
        <div className="flex items-center space-x-2">
          {renderDownloadButton()}
        </div>
      </div>
    </div>
  );
};

export default CanceledReturn;
